/**
 * diagnosticsModel — pure selectors for the IPD Diagnostics card (M2).
 *
 * ⚠ THE FINDING THIS FILE IS BUILT ON: `_stamp_order_paid` writes
 * payment_id/receipt_number/payment_cleared_at and DELIBERATELY leaves
 * `status` untouched — so a paid lab order still reads `status: 'advised'`
 * for ever. Row state is therefore derived from TWO fields, never from
 * `status` alone: cancelled wins (status === 'cancelled'), then paid
 * (server-derived `is_paid`, itself computed from `payment_id`), else
 * unpaid. A reader that checked `status` to decide paid/unpaid would mark
 * every paid test as unpaid.
 *
 * ONE derivation feeds both the row chips and the running totals — never
 * re-computed inline per surface (house rule: single source of truth for
 * derived UI values).
 */

export const DIAGNOSTICS_ROW_STATE = {
  CANCELLED: 'cancelled',
  PAID: 'paid',
  UNPAID: 'unpaid',
};

/**
 * @param {object} order a LabOrderResponse row
 * @param {boolean} [moneyHidden] the list's `money_visible === false` — paid/unpaid
 *   is a money signal the server withheld (`is_paid` arrives `null`), so a live row
 *   has NO state at all (`null`); a cancelled row is not money and stays cancelled.
 * @returns {'cancelled'|'paid'|'unpaid'|null}
 */
export function diagnosticsRowState(order, moneyHidden = false) {
  if (!order) return DIAGNOSTICS_ROW_STATE.UNPAID;
  if (order.status === 'cancelled') return DIAGNOSTICS_ROW_STATE.CANCELLED;
  if (moneyHidden) return null;
  if (order.is_paid) return DIAGNOSTICS_ROW_STATE.PAID;
  return DIAGNOSTICS_ROW_STATE.UNPAID;
}

/**
 * Running totals for the stay — "Total charged" and "Unpaid" — BOTH exclude
 * cancelled rows, so the card agrees with the diagnostics bill (M1 excludes
 * cancelled rows from the bill and its total). `final_cost` is rupees
 * (float), not paise — matches the LabOrderResponse contract.
 *
 * A live row whose `final_cost` is null (money withheld by the server) makes
 * the total unknowable: returns `null` — never a fake ₹0.
 */
export function diagnosticsTotals(orders) {
  const list = Array.isArray(orders) ? orders : [];
  let totalCharged = 0;
  let unpaid = 0;
  for (const o of list) {
    const state = diagnosticsRowState(o);
    if (state === DIAGNOSTICS_ROW_STATE.CANCELLED) continue;
    if (o.final_cost == null) return null;
    const cost = Number(o.final_cost) || 0;
    totalCharged += cost;
    if (state === DIAGNOSTICS_ROW_STATE.UNPAID) unpaid += cost;
  }
  return { totalCharged, unpaid };
}

/** Newest-first by `created_at` (falls back to 0 for a missing timestamp). */
export function sortNewestFirst(orders) {
  const list = Array.isArray(orders) ? orders : [];
  return [...list].sort((a, b) => {
    const ta = a?.created_at ? Date.parse(a.created_at) : 0;
    const tb = b?.created_at ? Date.parse(b.created_at) : 0;
    return tb - ta;
  });
}

/** Case-insensitive substring match on the test name. */
export function filterBySearch(orders, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return orders;
  return (orders || []).filter((o) => (o.test_name || '').toLowerCase().includes(q));
}

/**
 * Attender display — `name`/`relation`/`mobile` only (NEVER `attender.phone`;
 * the schema carries only `mobile`, `extra="forbid"`, so `phone` can never
 * exist — reading it would silently always render nothing).
 */
export function describeAttender(attender) {
  if (!attender || !attender.name) return { present: false };
  return {
    present: true,
    name: attender.name,
    relation: attender.relation || null,
    mobile: attender.mobile || null,
  };
}
